/**
 * VisibleWatermarkSection
 *
 * Opt-in sichtbares EU-KI-Label (Art. 50 Abs. 4 AI Act) für Studio-Generierungen.
 * Default aus — Produktfotos bleiben unangetastet, sofern der Nutzer nicht aktiviert.
 */
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

export type WatermarkPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface VisibleWatermarkSectionProps {
  enabled: boolean;
  position: WatermarkPosition;
  onEnabledChange: (enabled: boolean) => void;
  onPositionChange: (position: WatermarkPosition) => void;
}

const POSITIONS: Array<{ value: WatermarkPosition; labelKey: string }> = [
  { value: 'top-left', labelKey: 'positionTopLeft' },
  { value: 'top-right', labelKey: 'positionTopRight' },
  { value: 'bottom-left', labelKey: 'positionBottomLeft' },
  { value: 'bottom-right', labelKey: 'positionBottomRight' },
];

export function VisibleWatermarkSection({
  enabled,
  position,
  onEnabledChange,
  onPositionChange,
}: VisibleWatermarkSectionProps) {
  const { t } = useTranslation();
  const w = (key: string) => t(`generation.visibleWatermark.${key}`);

  return (
    <div className="space-y-3">
      <Switch
        label={w('toggleLabel')}
        helperText={w('toggleDescription')}
        checked={enabled}
        onCheckedChange={onEnabledChange}
      />

      {enabled && (
        <div>
          <span className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {w('positionLabel')}
          </span>
          <div role="radiogroup" aria-label={w('positionLabel')} className="grid grid-cols-2 gap-2">
            {POSITIONS.map(({ value, labelKey }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={position === value}
                onClick={() => onPositionChange(value)}
                className={cn(
                  'min-h-11 px-3 py-2 rounded-lg border-2 text-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                  position === value
                    ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-950 dark:text-primary-300'
                    : 'border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                )}
              >
                {w(labelKey)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
